import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { combineLatest, map, Observable, of, Subscription } from 'rxjs'

import { anchorKey, EditorThread } from '../editor-thread.types'
import { collectFormattingLabelDescriptors, collectListChangeLabelDescriptors, SuggestionSummary, SuggestionTextRun } from '../extensions/track-changes-utils'
import { formatRelativeTime } from '../utils/format-relative-time'
import { ThreadCommentsCurrentUser } from './thread-comments/thread-comments.component'
import { SuggestionCardComponent, SuggestionCardVariant } from './suggestion-card/suggestion-card.component'

type SidebarTab = 'all' | 'changes'

const HIGHLIGHT_DURATION_MS = 1500

type SuggestionCardModel = {
  id: string
  variant: SuggestionCardVariant
  authorName: string
  createdAtRelative: string | null
  insertionRuns: SuggestionTextRun[]
  deletionRuns: SuggestionTextRun[]
  formattingText: string
  structureText: string
  thread: EditorThread | null
}

const TABS: { id: SidebarTab; labelKey: string }[] = [
  { id: 'all', labelKey: 'EDITOR.TRACK_CHANGES.SIDEBAR.TAB_ALL' },
  { id: 'changes', labelKey: 'EDITOR.TRACK_CHANGES.SIDEBAR.TAB_CHANGES' },
]

@Component({
  selector: 'app-tiptap-editor-sidebar',
  standalone: true,
  imports: [CommonModule, TranslatePipe, SuggestionCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tiptap-editor-sidebar.component.html',
  styleUrl: './tiptap-editor-sidebar.component.scss',
})
export class TiptapEditorSidebarComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input({ required: true })
  suggestions$!: Observable<SuggestionSummary[]>

  @Input({ required: true })
  activeSuggestionId$!: Observable<string | null>

  @Input({ required: true })
  threads$!: Observable<Map<string, EditorThread>>

  @Input({ required: true })
  canResolve!: boolean

  @Input({ required: true })
  currentUser!: ThreadCommentsCurrentUser

  @Input()
  canPostComment = true

  @Output()
  accept = new EventEmitter<string>()

  @Output()
  reject = new EventEmitter<string>()

  @Output()
  view = new EventEmitter<string>()

  @Output()
  submitComment = new EventEmitter<{ suggestionId: string; body: string }>()

  @ViewChildren('cardRef', { read: ElementRef })
  cardRefs: QueryList<ElementRef<HTMLElement>> = new QueryList()

  readonly tabs = TABS

  selectedTab: SidebarTab = 'all'
  highlightedSuggestionId: string | null = null
  cards$: Observable<SuggestionCardModel[]> = of([])

  private translateService = inject(TranslateService)
  private changeDetectorRef = inject(ChangeDetectorRef)
  private destroyRef = inject(DestroyRef)
  private activeSuggestionSubscription: Subscription | null = null
  private highlightTimeoutId: number | null = null

  ngOnChanges(changes: SimpleChanges) {
    if ('suggestions$' in changes || 'threads$' in changes) {
      this.cards$ = combineLatest([this.suggestions$, this.threads$]).pipe(map(([summaries, threads]) => this.summariesToCards(summaries, threads)))
    }
    if ('activeSuggestionId$' in changes) {
      this.activeSuggestionSubscription?.unsubscribe()
      this.activeSuggestionSubscription = this.activeSuggestionId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
        if (id !== null) {
          this.highlightCard(id)
        }
      })
    }
  }

  ngAfterViewInit() {
    if (this.highlightedSuggestionId !== null) {
      this.scrollHighlightedCardIntoView()
    }
  }

  ngOnDestroy() {
    if (this.highlightTimeoutId !== null) {
      clearTimeout(this.highlightTimeoutId)
    }
  }

  selectTab(tab: SidebarTab) {
    this.selectedTab = tab
    this.changeDetectorRef.markForCheck()
  }

  onAccept(suggestionId: string) {
    this.accept.emit(suggestionId)
  }

  onReject(suggestionId: string) {
    this.reject.emit(suggestionId)
  }

  onView(suggestionId: string) {
    this.view.emit(suggestionId)
  }

  onSubmitComment(suggestionId: string, body: string) {
    this.submitComment.emit({ suggestionId, body })
  }

  private summariesToCards(summaries: SuggestionSummary[], threads: Map<string, EditorThread>): SuggestionCardModel[] {
    const fallbackAuthor = this.translateService.instant('EDITOR.TRACK_CHANGES.UNKNOWN_AUTHOR')
    return summaries.map((summary) => this.toCardModel(summary, fallbackAuthor, threads))
  }

  private toCardModel(summary: SuggestionSummary, fallbackAuthor: string, threads: Map<string, EditorThread>): SuggestionCardModel {
    const formattingText = summary.category === 'formatting' ? this.formatFormattingLabels(summary.formattingAdded, summary.formattingRemoved) : ''
    const structureText = this.formatListChangeLabels(summary.listChanges)

    return {
      id: summary.id,
      variant: summary.category,
      authorName: summary.authorName ?? fallbackAuthor,
      createdAtRelative: formatRelativeTime(summary.createdAt),
      insertionRuns: summary.insertionRuns,
      deletionRuns: summary.deletionRuns,
      formattingText,
      structureText,
      thread: threads.get(anchorKey('SUGGESTION', summary.id)) ?? null,
    }
  }

  private formatFormattingLabels(added: string[], removed: string[]): string {
    return collectFormattingLabelDescriptors(added, removed)
      .map((descriptor) =>
        this.translateService.instant(
          descriptor.negated ? `EDITOR.TRACK_CHANGES.FORMAT_NOT_${descriptor.key}` : `EDITOR.TRACK_CHANGES.FORMAT_${descriptor.key}`
        )
      )
      .join(', ')
  }

  private formatListChangeLabels(changes: SuggestionSummary['listChanges']): string {
    return collectListChangeLabelDescriptors(changes)
      .map((descriptor) => this.translateService.instant(`EDITOR.TRACK_CHANGES.${descriptor.key}`, descriptor.params))
      .join(', ')
  }

  private highlightCard(suggestionId: string) {
    const sameId = this.highlightedSuggestionId === suggestionId
    if (!sameId) {
      this.highlightedSuggestionId = suggestionId
      this.changeDetectorRef.markForCheck()
    }
    this.scrollHighlightedCardIntoView()

    if (this.highlightTimeoutId !== null) {
      clearTimeout(this.highlightTimeoutId)
    }
    this.highlightTimeoutId = window.setTimeout(() => {
      this.highlightedSuggestionId = null
      this.highlightTimeoutId = null
      this.changeDetectorRef.markForCheck()
    }, HIGHLIGHT_DURATION_MS)
  }

  private scrollHighlightedCardIntoView() {
    if (this.highlightedSuggestionId === null) {
      return
    }
    const targetId = this.highlightedSuggestionId
    queueMicrotask(() => {
      const card = this.cardRefs.find((ref) => ref.nativeElement.dataset['suggestionId'] === targetId)
      if (card !== undefined) {
        card.nativeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    })
  }
}
